import * as React from "react";
import { Checkbox, HTMLTable } from "@blueprintjs/core";

import GenomeRow from "./row";
import { GENOMES_PER_PAGE } from "../../../utils/constants";
import LoadingRow from "./loading-row";
import { CollectionName, GenomeInList, GenomesOrderBy } from "../../../api";
import GenomesTablePaginator from "../../paginator/genomes-table";
import OrderByIcon from "../../order-by-icon";
import { observer } from "mobx-react";
import { useContext } from "react";
import { StoresContext } from "../../../pages/_app";

interface GenomesTableProps {
  collectionName: CollectionName | null;
  loading: boolean;
  genomes: GenomeInList[];
  totalCount: number;
  orderBy: GenomesOrderBy;
  isAscending: boolean;
  changePage: (page: number) => void;
  page: number;
  filterByTag: (tag: string) => void;
  toggleGenomeStarring: (starred: boolean, uuid: string) => void;
  changeOrderBy: (sortTerm: GenomesOrderBy) => void;
}

const GenomesTable = ({
  loading,
  genomes,
  collectionName,
  isAscending,
  orderBy,
  totalCount,
  page,
  filterByTag,
  toggleGenomeStarring,
  changeOrderBy,
  changePage,
}: GenomesTableProps) => {
  const rootStore = useContext(StoresContext);
  const { isPageSelected } = rootStore.genomeStore;

  const rows: React.ReactNode[] = [];
  if (loading) {
    const numColumns = 9;
    for (let i = 0; i < GENOMES_PER_PAGE; i++) {
      rows.push(<LoadingRow numColumns={numColumns} key={`loading-${i}`} />);
    }
  } else {
    genomes.forEach((genome, index) => {
      rows.push(
        <GenomeRow
          key={genome.uuid}
          collectionName={collectionName}
          genome={genome}
          filterByTag={filterByTag}
          toggleGenomeStarring={toggleGenomeStarring}
          rowNum={index + 1}
        />
      );
    });
  }

  const handleSelectAllInPageClicked = () => {
    rootStore.genomeStore.togglePageSelected(genomes.map((g) => g.uuid));
  };

  const className = rootStore.genomeStore.sequenceSearchEnabled ? "result-0" : "";

  const productPageHeader = (
    <th className="clickable" onClick={() => changeOrderBy("name")}>
      ATCC Product Page
      <OrderByIcon isActive={orderBy === "name"} isAscending={isAscending} />
    </th>
  );

  return (
    <>
      <HTMLTable className="genomes-table" striped={true}>
        <thead>
          <tr>
            {/* Header for checkbox */}
            <th className={`genomes-table__checkbox ${className}`}>
              <Checkbox
                checked={isPageSelected(genomes.map((g) => g.uuid))}
                onChange={handleSelectAllInPageClicked}
              />
            </th>
            {/* Header for starring */}
            <th className="genomes-table__star" />
            <th className="clickable" onClick={() => changeOrderBy("taxon.name")}>
              Taxonomic Name
              <OrderByIcon isActive={orderBy === "taxon.name"} isAscending={isAscending} />
            </th>
            {productPageHeader}
            {rootStore.genomeStore.relatedGenomeSearchEnabled && (
              <th className="clickable" onClick={() => changeOrderBy("genome_similarity")}>
                {rootStore.genomeStore.sequenceSearchEnabled ? "Genome Similarity" : "Similarity"}
                <OrderByIcon isActive={orderBy === "genome_similarity"} isAscending={isAscending} />
              </th>
            )}
            {rootStore.genomeStore.sequenceSearchEnabled && (
              <th className="clickable" onClick={() => changeOrderBy("search_similarity")}>
                {rootStore.genomeStore.relatedGenomeSearchEnabled
                  ? "Search Similarity"
                  : "Similarity"}
                <OrderByIcon isActive={orderBy === "search_similarity"} isAscending={isAscending} />
              </th>
            )}
            <th>Strain Alias</th>
            <th>Tags</th>
            <th className="clickable" onClick={() => changeOrderBy("created_at")}>
              Date Published
              <OrderByIcon isActive={orderBy === "created_at"} isAscending={isAscending} />
            </th>
            <th>Length</th>
          </tr>
        </thead>
        <tbody>{rows}</tbody>
      </HTMLTable>
      <GenomesTablePaginator totalCount={totalCount} page={page} onPageChange={changePage} />
    </>
  );
};

export default observer(GenomesTable);
